import React, { useEffect, useState, useCallback } from 'react';
import { DateInput, DateTimeInput } from '@/components/ui/date-input';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import ChannelDisplayNameField from '../components/ChannelDisplayNameField';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Loader2, Plug, CheckCircle2, XCircle, Plus, Trash2,
  RefreshCw, ShoppingCart, ExternalLink, AlertTriangle, Package2,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COUNTRY_META = {
  amazon_in: { label: 'Amazon India', flag: '🇮🇳', hub: 'https://sellercentral.amazon.in', currency: 'INR', hubName: 'Seller Central India' },
  amazon_ae: { label: 'Amazon UAE', flag: '🇦🇪', hub: 'https://sellercentral.amazon.ae', currency: 'AED', hubName: 'Seller Central UAE' },
  amazon_sa: { label: 'Amazon Saudi Arabia', flag: '🇸🇦', hub: 'https://sellercentral.amazon.sa', currency: 'SAR', hubName: 'Seller Central Saudi Arabia' },
  amazon_eg: { label: 'Amazon Egypt', flag: '🇪🇬', hub: 'https://sellercentral.amazon.eg', currency: 'EGP', hubName: 'Seller Central Egypt' },
  amazon_tr: { label: 'Amazon Turkey', flag: '🇹🇷', hub: 'https://sellercentral.amazon.com.tr', currency: 'TRY', hubName: 'Seller Central Turkey' },
  amazon_uk: { label: 'Amazon UK', flag: '🇬🇧', hub: 'https://sellercentral.amazon.co.uk', currency: 'GBP', hubName: 'Seller Central UK' },
  amazon_de: { label: 'Amazon Germany', flag: '🇩🇪', hub: 'https://sellercentral.amazon.de', currency: 'EUR', hubName: 'Seller Central Germany' },
  amazon_fr: { label: 'Amazon France', flag: '🇫🇷', hub: 'https://sellercentral.amazon.fr', currency: 'EUR', hubName: 'Seller Central France' },
  amazon_it: { label: 'Amazon Italy', flag: '🇮🇹', hub: 'https://sellercentral.amazon.it', currency: 'EUR', hubName: 'Seller Central Italy' },
  amazon_es: { label: 'Amazon Spain', flag: '🇪🇸', hub: 'https://sellercentral.amazon.es', currency: 'EUR', hubName: 'Seller Central Spain' },
  amazon_nl: { label: 'Amazon Netherlands', flag: '🇳🇱', hub: 'https://sellercentral.amazon.nl', currency: 'EUR', hubName: 'Seller Central Netherlands' },
  amazon_us: { label: 'Amazon US', flag: '🇺🇸', hub: 'https://sellercentral.amazon.com', currency: 'USD', hubName: 'Seller Central US' },
  amazon_ca: { label: 'Amazon Canada', flag: '🇨🇦', hub: 'https://sellercentral.amazon.ca', currency: 'CAD', hubName: 'Seller Central Canada' },
  amazon_mx: { label: 'Amazon Mexico', flag: '🇲🇽', hub: 'https://sellercentral.amazon.com.mx', currency: 'MXN', hubName: 'Seller Central Mexico' },
  amazon_jp: { label: 'Amazon Japan', flag: '🇯🇵', hub: 'https://sellercentral.amazon.co.jp', currency: 'JPY', hubName: 'Seller Central Japan' },
  amazon_au: { label: 'Amazon Australia', flag: '🇦🇺', hub: 'https://sellercentral.amazon.com.au', currency: 'AUD', hubName: 'Seller Central Australia' },
  amazon_sg: { label: 'Amazon Singapore', flag: '🇸🇬', hub: 'https://sellercentral.amazon.sg', currency: 'SGD', hubName: 'Seller Central Singapore' },
};

export default function AmazonIntegrationPage() {
  const { country } = useParams(); // "in", "ae", "sa", "us", etc.
  const channelType = `amazon_${country}`;
  const meta = COUNTRY_META[channelType];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [productSyncJob, setProductSyncJob] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const token = localStorage.getItem('token');
  const headers = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const [cfgRes, accRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/integrations/amazon/config`, headers()),
        axios.get(`${API_BASE_URL}/integrations/amazon/accounts`, headers()),
      ]);
      setConfig(cfgRes.data);
      const list = (accRes.data?.grouped || {})[channelType] || [];
      setAccounts(list);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Amazon config');
    } finally {
      setLoading(false);
    }
  }, [channelType, meta, headers]);

  useEffect(() => {
    load();
  }, [load]);

  // After OAuth round-trip, Amazon lands the user here with ?connected=1.
  // Or ?error=<reason> on failure. Toast + clean the URL.
  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      toast.success(`Connected to ${meta?.label} via Amazon`);
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      setSearchParams(next, { replace: true });
      load();
    } else if (searchParams.get('error')) {
      const code = searchParams.get('error');
      const messages = {
        missing_params: 'Amazon did not return an authorization code. Please retry.',
        invalid_state: 'The authorization link expired. Please retry the connection.',
        lwa_exchange_failed: 'Amazon rejected the token exchange — please retry.',
        no_refresh_token: 'Amazon did not issue a refresh token. Ensure the app has the right roles and retry.',
      };
      toast.error(messages[code] || `Connection failed (${code})`);
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const syncOrdersNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/orders/sync-now`,
        {},
        headers()
      );
      if (r.data?.status === 'skipped') {
        toast.info('No active Amazon accounts to sync');
      } else {
        const forMe = (r.data?.accounts || []).filter((a) => a.country === channelType);
        const ingested = forMe.reduce((s, a) => s + (a.ingested || 0), 0);
        toast.success(`Synced — ${ingested} new ${meta.label} order${ingested === 1 ? '' : 's'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Pull every Amazon listing into `channel_listings` — SKUs that
  // exactly match inventory master are auto-mapped; the rest land in
  // the "Unmapped" tab of /inventory/channel-listings for manual
  // mapping. Backend now runs the sweep as a BACKGROUND task because
  // tenants with 3000+ SKUs were timing out the gateway. We get back
  // a sync_id, then poll `/sync-status` every 3s for live progress.
  const showSyncFinishedToast = useCallback((job) => {
    const totals = job?.totals || {};
    const failed = (job?.accounts || []).filter((a) => !a.ok);
    if (job?.status === 'failed') {
      toast.error(`Amazon product sync failed: ${job.error || 'Unknown error'}`);
    } else if ((totals.synced || 0) > 0) {
      toast.success(
        `Synced ${totals.synced} listings — ${totals.auto_mapped || 0} auto-mapped, ${totals.unmapped || 0} need mapping`,
        {
          action: (totals.unmapped || 0) > 0
            ? { label: 'Open Unmapped', onClick: () => navigate('/inventory/channel-listings') }
            : undefined,
          duration: 8000,
        }
      );
    } else {
      toast.info('No Amazon listings found on connected accounts');
    }
    failed.forEach((a) => {
      toast.error(`${a.account_name || a.account_id}: ${a.error}`);
    });
  }, [navigate]);

  const pollSyncStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_BASE_URL}/integrations/amazon/products/sync-status`,
        headers()
      );
      const job = r.data?.job;
      if (!job) {
        setSyncingProducts(false);
        setProductSyncJob(null);
        return null;
      }
      setProductSyncJob(job);
      if (job.status !== 'running') {
        setSyncingProducts(false);
        showSyncFinishedToast(job);
      }
      return job;
    } catch (err) {
      // Don't kill the loop on transient network blips — the next tick will retry.
      return null;
    }
  }, [headers, showSyncFinishedToast]);

  // Poll while a sync is in flight. The interval clears itself when
  // the job leaves the running state (or component unmounts).
  useEffect(() => {
    if (!syncingProducts) return undefined;
    const id = setInterval(() => {
      pollSyncStatus();
    }, 3000);
    return () => clearInterval(id);
  }, [syncingProducts, pollSyncStatus]);

  // On mount, recover any in-flight job so refresh / nav-away-and-back
  // doesn't leave the user blind to a sweep that's still chugging along.
  useEffect(() => {
    if (!meta) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API_BASE_URL}/integrations/amazon/products/sync-status`,
          headers()
        );
        const job = r.data?.job;
        if (job && job.status === 'running') {
          setSyncingProducts(true);
          setProductSyncJob(job);
        }
      } catch {/* ignore — non-critical */}
    })();
  }, [meta, headers]);

  const syncProductsNow = async () => {
    setSyncingProducts(true);
    setProductSyncJob(null);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/products/sync-now`,
        {},
        headers()
      );
      // Legacy / blocking path — server returned full totals synchronously.
      if (!r.data?.background) {
        const totals = r.data?.totals || {};
        const failed = (r.data?.accounts || []).filter((a) => !a.ok);
        if (totals.synced > 0) {
          toast.success(
            `Synced ${totals.synced} listings — ${totals.auto_mapped} auto-mapped, ${totals.unmapped} need mapping`,
            { duration: 8000 }
          );
        } else {
          toast.info('No Amazon listings found on connected accounts');
        }
        failed.forEach((a) => {
          toast.error(`${a.account_name}: ${a.error}`);
        });
        setSyncingProducts(false);
        return;
      }
      // Background path — kick off polling.
      if (r.data?.already_running) {
        toast.info('A product sync is already running — polling progress…');
      } else {
        toast.info('Amazon product sync started — this can take a few minutes for large catalogues.');
      }
      // Prime the job state immediately so the progress chip renders.
      pollSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Product sync failed');
      setSyncingProducts(false);
    }
  };

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Unknown Amazon country: {country}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg">
      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/integrations')}
          className="mb-4 text-wt-text-muted hover:text-wt-text-primary"
          data-testid="amazon-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Integrations
        </Button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#232F3E] to-[#FF9900] flex items-center justify-center text-white shadow-lg">
              <ShoppingCart className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-wt-text-primary flex items-center gap-2">
                <span>{meta.flag}</span>
                {meta.label}
              </h1>
              <p className="text-sm text-wt-text-muted">
                Multi-account SP-API integration • Currency {meta.currency}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={meta.hub}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-wt-text-muted hover:text-wt-gold flex items-center gap-1"
            >
              {meta.hubName}
              <ExternalLink className="w-3 h-3" />
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={syncOrdersNow}
              disabled={syncing || accounts.filter((a) => a.is_active).length === 0}
              data-testid="amazon-sync-orders-btn"
            >
              {syncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Orders Now
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={syncProductsNow}
              disabled={syncingProducts || accounts.filter((a) => a.is_active).length === 0}
              data-testid="amazon-sync-products-btn"
              title="Pull every SKU from Amazon into Channel Listings. SKUs matching the inventory master are auto-mapped; the rest land in the Unmapped tab."
            >
              {syncingProducts ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Package2 className="h-4 w-4 mr-2" />
              )}
              {syncingProducts && productSyncJob?.status === 'running'
                ? `Syncing… ${productSyncJob?.totals?.synced || 0} listings`
                : 'Sync Products'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  const r = await axios.post(
                    `${API_BASE_URL}/integrations/amazon/orders/backfill-totals`,
                    {},
                    headers()
                  );
                  toast.success(`Re-parsed tax/shipping on ${r.data.fixed} orders`);
                  if (r.data.errors?.length) {
                    toast.warning(`${r.data.errors.length} orders failed — check console`);
                    console.warn('backfill errors', r.data.errors);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Backfill failed');
                }
              }}
              disabled={accounts.filter((a) => a.is_active).length === 0}
              title="Re-fetch tax / shipping / discount amounts from Amazon for existing orders missing them"
              data-testid="amazon-backfill-totals-btn"
              className="text-wt-text-muted hover:text-wt-gold"
            >
              Fix Tax
            </Button>
            {config?.oauth_enabled && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  // Amazon's consent page is a full-page redirect — can't
                  // use fetch. Build the start URL with our JWT so the
                  // backend can scope the callback to this tenant.
                  const token = localStorage.getItem('token');
                  const url =
                    `${API_BASE_URL}/integrations/amazon/oauth/start` +
                    `?channel_type=${channelType}` +
                    `&account_name=${encodeURIComponent(`${meta.label} account`)}` +
                    `&_jwt=${encodeURIComponent(token)}`;
                  window.location.href = url;
                }}
                className="bg-gradient-to-r from-[#FF9900] to-[#FFAC31] text-black hover:opacity-90"
                data-testid="amazon-connect-oauth-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Connect with Amazon
              </Button>
            )}
            <Button
              onClick={() => setAddOpen(true)}
              variant={config?.oauth_enabled ? 'outline' : 'default'}
              data-testid="amazon-add-account-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {config?.oauth_enabled ? 'Paste Credentials' : 'Add Account'}
            </Button>
          </div>
        </div>

        {/* App-level config banner */}
        {config && !config.app_configured && (
          <Card className="p-4 mb-6 border-red-500/40 bg-red-500/10">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-300">
                  Opswarex Amazon app not configured
                </p>
                <p className="text-xs text-red-300/80 mt-1">
                  Admin needs to set AMAZON_LWA_CLIENT_ID &amp; AMAZON_LWA_CLIENT_SECRET
                  in backend environment. Contact support.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Instructions card */}
        <Card className="p-4 mb-6 border-amber-500/30 bg-amber-500/10">
          <p className="text-sm font-medium text-amber-200 mb-2">
            How to get your Refresh Token from {meta.hubName}
          </p>
          <ol className="text-xs text-amber-200/80 space-y-1 list-decimal ml-5">
            <li>
              Log in to <a href={meta.hub} target="_blank" rel="noreferrer" className="underline">{meta.hub}</a> as primary user.
            </li>
            <li>Hamburger → <strong>Partner Network → Develop Apps</strong>.</li>
            <li>Register as Private Developer (instant for self-use apps).</li>
            <li>Add new app with roles: Product Listing, Pricing, Amazon Fulfilment, Inventory &amp; Order Tracking.</li>
            <li>Click <strong>Authorize</strong> next to your app → <strong>Generate Refresh Token</strong> (starts with <code>Atzr|...</code>).</li>
            <li>Copy the <strong>Merchant Token</strong> (Seller ID) from Settings → Account Info.</li>
            <li>Paste both into the Add Account form here.</li>
          </ol>
        </Card>

        {/* Accounts list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-wt-gold" />
          </div>
        ) : accounts.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <Plug className="w-10 h-10 mx-auto text-wt-text-muted mb-3" />
            <p className="text-sm text-wt-text-primary font-medium">
              No {meta.label} accounts connected yet
            </p>
            <p className="text-xs text-wt-text-muted mt-1 mb-4">
              Add your first seller account to start syncing orders + inventory.
            </p>
            <Button onClick={() => setAddOpen(true)} data-testid="amazon-empty-add-btn">
              <Plus className="h-4 w-4 mr-2" /> Add {meta.label} Account
            </Button>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="amazon-accounts-list">
            {accounts.map((acc) => (
              <AccountCard
                key={acc.id}
                account={acc}
                meta={meta}
                onRefresh={load}
                onDelete={() => setConfirmDelete(acc)}
                headers={headers}
              />
            ))}
          </div>
        )}

        <AddAccountDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          channelType={channelType}
          meta={meta}
          onAdded={() => { setAddOpen(false); load(); }}
          headers={headers}
        />

        {/* Easy-Ship default-slot preferences — India only (Phase 1 of Easy-Ship) */}
        {channelType === 'amazon_in' && accounts.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="amazon-easyship-preferences">
            <div className="flex items-center gap-2">
              <Plug className="w-4 h-4 text-[#FF9900]" />
              <h2 className="text-sm font-medium text-wt-text-primary">
                Easy-Ship default pickup slots
              </h2>
            </div>
            <p className="text-xs text-wt-text-muted -mt-1">
              Save a default pickup slot per seller account so future Easy-Ship
              labels can auto-schedule without picking a slot each time.
            </p>
            {accounts.map((acc) => (
              <EasyShipPreferenceCard
                key={`es-${acc.id}`}
                account={acc}
                headers={headers}
                onChanged={load}
              />
            ))}
          </div>
        )}

        <AlertDialog
          open={!!confirmDelete}
          onOpenChange={(o) => !o && setConfirmDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Amazon account?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes <strong>{confirmDelete?.account_name}</strong> (seller {' '}
                <code>{confirmDelete?.seller_id}</code>) from Opswarex. Orders already ingested will
                remain, but no new orders or inventory pushes will happen until you re-connect.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  try {
                    await axios.delete(
                      `${API_BASE_URL}/integrations/amazon/accounts/${confirmDelete.id}`,
                      headers()
                    );
                    toast.success('Account removed');
                    setConfirmDelete(null);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to remove');
                  }
                }}
                data-testid="amazon-confirm-delete-btn"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function AccountCard({ account, meta, onRefresh, onDelete, headers }) {
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const onTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/accounts/${account.id}/test`,
        {},
        headers()
      );
      if (r.data?.ok) {
        toast.success(`${account.account_name} — live connection OK`);
      } else {
        toast.error(`Connection failed (status ${r.data?.status})`);
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const onToggleActive = async (next) => {
    setToggling(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/integrations/amazon/accounts/${account.id}`,
        { is_active: next },
        headers()
      );
      toast.success(next ? 'Account enabled' : 'Account paused');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setToggling(false);
    }
  };

  const statusOK = account.last_test_status === 'ok';

  return (
    <Card
      className="p-4"
      data-testid={`amazon-account-row-${account.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-medium text-wt-text-primary truncate">
              {account.account_name}
            </h3>
            {statusOK ? (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-xs">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-500/15 text-red-300 border-red-500/30 text-xs">
                <XCircle className="w-3 h-3 mr-1" /> Error
              </Badge>
            )}
            {!account.is_active && (
              <Badge variant="outline" className="bg-wt-subtle text-wt-text-muted border-wt-border text-xs">
                Paused
              </Badge>
            )}
          </div>
          <div className="text-xs text-wt-text-muted mt-1 space-x-3">
            <span>Seller: <code className="text-wt-text-primary">{account.seller_id}</code></span>
            <span>Marketplace: <code className="text-wt-text-primary">{account.marketplace_id}</code></span>
            {account.last_tested_at && (
              <span>Last test: {new Date(account.last_tested_at).toLocaleString()}</span>
            )}
          </div>
          <div className="mt-2">
            <ChannelDisplayNameField channel={account} onSaved={onRefresh} />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1 text-xs text-wt-text-muted">
            Active
            <Switch
              checked={account.is_active}
              disabled={toggling}
              onCheckedChange={onToggleActive}
              data-testid={`amazon-account-active-toggle-${account.id}`}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onTest}
            disabled={testing}
            data-testid={`amazon-test-btn-${account.id}`}
          >
            {testing ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="text-red-400 hover:text-red-300"
            data-testid={`amazon-delete-btn-${account.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Sync settings — mirror Shopify's toggle grid for parity. */}
      <div
        className="mt-4 pt-4 border-t border-wt-border grid grid-cols-1 sm:grid-cols-3 gap-3"
        data-testid={`amazon-account-sync-settings-${account.id}`}
      >
        <SyncToggle
          label="Order Sync"
          description="Auto-pull new Amazon orders every 5 minutes."
          checked={account.orders_sync_enabled !== false}
          onChange={(v) => patchAccount(account, headers, { orders_sync_enabled: v }, onRefresh)}
          testid={`amazon-orders-sync-toggle-${account.id}`}
        />
        <SyncToggle
          label="Inventory Sync"
          description="Push Opswarex stock changes to Amazon listings."
          checked={account.inventory_sync_enabled !== false}
          onChange={(v) => patchAccount(account, headers, { inventory_sync_enabled: v }, onRefresh)}
          testid={`amazon-inventory-sync-toggle-${account.id}`}
        />
        <SyncSinceDate
          value={account.orders_sync_since_date || ''}
          onChange={(v) => patchAccount(account, headers, { orders_sync_since_date: v }, onRefresh)}
          testid={`amazon-orders-sync-since-${account.id}`}
        />
      </div>
    </Card>
  );
}

// Shared PATCH helper — lives next to AccountCard so it stays in sync.
async function patchAccount(account, headers, updates, onRefresh) {
  try {
    await axios.patch(
      `${API_BASE_URL}/integrations/amazon/accounts/${account.id}`,
      updates,
      headers()
    );
    toast.success('Saved');
    if (onRefresh) onRefresh();
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Failed to save');
  }
}

function SyncToggle({ label, description, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 rounded-md bg-wt-bg/50 border border-wt-border">
      <div className="min-w-0">
        <p className="text-xs font-medium text-wt-text-primary">{label}</p>
        <p className="text-[11px] text-wt-text-muted mt-0.5 leading-snug">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </div>
  );
}

function SyncSinceDate({ value, onChange, testid }) {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);

  const save = () => {
    if (local !== (value || '')) onChange(local);
  };

  return (
    <div className="p-3 rounded-md bg-wt-bg/50 border border-wt-border">
      <p className="text-xs font-medium text-wt-text-primary">Sync orders from</p>
      <p className="text-[11px] text-wt-text-muted mt-0.5 leading-snug">
        Only orders placed on/after this date will be ingested — prevents
        ancient orders from flooding Opswarex on first connect.
      </p>
      <div className="flex items-center gap-2 mt-2">
        <DateInput
          value={local}
          onChange={(e) => {
            setLocal(e.target.value);
            if (e.target.value !== (value || '')) onChange(e.target.value);
          }}
          className="w-full text-xs bg-wt-bg border border-wt-border rounded px-2 py-1 text-wt-text-primary"
          data-testid={testid}
        />
        {local && (
          <button
            type="button"
            onClick={() => { setLocal(''); onChange(''); }}
            className="text-[10px] text-wt-text-muted hover:text-red-400"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

function AddAccountDialog({ open, onOpenChange, channelType, meta, onAdded, headers }) {
  const [form, setForm] = useState({
    account_name: '',
    seller_id: '',
    refresh_token: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.account_name || !form.seller_id || !form.refresh_token) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/integrations/amazon/accounts`,
        { channel_type: channelType, ...form },
        headers()
      );
      const storeName =
        r.data?.test?.body?.payload?.[0]?.storeName || form.account_name;
      toast.success(`Connected — ${storeName}`);
      setForm({ account_name: '', seller_id: '', refresh_token: '' });
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="amazon-add-account-dialog">
        <DialogHeader>
          <DialogTitle>Add {meta.label} account</DialogTitle>
          <DialogDescription className="text-xs">
            Paste credentials from your {meta.hubName} private developer app.
            The connection is verified live against Amazon before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Account name</Label>
            <Input
              value={form.account_name}
              onChange={(e) => setForm({ ...form, account_name: e.target.value })}
              placeholder="e.g. Main India Store"
              data-testid="amazon-form-account-name"
            />
          </div>
          <div>
            <Label className="text-xs">Seller ID (Merchant Token)</Label>
            <Input
              value={form.seller_id}
              onChange={(e) => setForm({ ...form, seller_id: e.target.value.trim() })}
              placeholder="A1XXXXXXXXXXX"
              data-testid="amazon-form-seller-id"
            />
            <p className="text-[10px] text-wt-text-muted mt-1">
              Settings → Account Info → Merchant Token in {meta.hubName}
            </p>
          </div>
          <div>
            <Label className="text-xs">Refresh Token</Label>
            <textarea
              value={form.refresh_token}
              onChange={(e) => setForm({ ...form, refresh_token: e.target.value.trim() })}
              placeholder="Atzr|IwEBI..."
              rows={3}
              className="w-full text-xs font-mono bg-wt-bg border border-wt-border rounded px-3 py-2 focus:outline-none focus:border-wt-gold"
              data-testid="amazon-form-refresh-token"
            />
            <p className="text-[10px] text-wt-text-muted mt-1">
              Generated by clicking "Authorize" next to your app in Developer Central.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="amazon-form-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
            Connect &amp; Test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Easy-Ship default pickup-slot preference card (Amazon India only)
// ---------------------------------------------------------------------------
function EasyShipPreferenceCard({ account, headers, onChanged }) {
  const [pref, setPref] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_BASE_URL}/integrations/amazon/easyship/accounts/${account.id}/preference`,
        headers()
      );
      setPref(r.data?.preference || null);
    } catch {
      setPref(null);
    } finally {
      setLoading(false);
    }
  }, [account.id, headers]);

  useEffect(() => { load(); }, [load]);

  const clearPref = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/integrations/amazon/easyship/accounts/${account.id}/preference`,
        headers()
      );
      toast.success('Default slot cleared');
      setPref(null);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear');
    }
  };

  return (
    <Card
      className="p-4"
      data-testid={`amazon-easyship-pref-card-${account.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-wt-text-primary truncate">
            {account.account_name}
          </p>
          <p className="text-xs text-wt-text-muted">
            {loading ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </span>
            ) : pref?.slot_start_time ? (
              <>
                Default slot: <span className="text-wt-text-primary">{pref.description}</span>
                {' '}• saved {new Date(pref.updated_at).toLocaleDateString()}
              </>
            ) : (
              <span className="text-amber-300">No default slot — user will pick per order</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid={`amazon-easyship-pref-set-btn-${account.id}`}
          >
            {pref?.slot_start_time ? 'Change' : 'Set default'}
          </Button>
          {pref?.slot_start_time && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearPref}
              className="text-red-400 hover:text-red-300"
              data-testid={`amazon-easyship-pref-clear-btn-${account.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <EasyShipPreferenceDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        account={account}
        headers={headers}
        onSaved={() => {
          setEditOpen(false);
          load();
          if (onChanged) onChanged();
        }}
      />
    </Card>
  );
}

function EasyShipPreferenceDialog({ open, onOpenChange, account, headers, onSaved }) {
  const [form, setForm] = useState({
    slot_start_time: '',
    slot_end_time: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.slot_start_time || !form.slot_end_time) {
      toast.error('Start and end time are required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/integrations/amazon/easyship/accounts/${account.id}/preference`,
        form,
        headers()
      );
      toast.success('Default slot saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="amazon-easyship-pref-dialog">
        <DialogHeader>
          <DialogTitle>Set default Easy-Ship pickup slot</DialogTitle>
          <DialogDescription className="text-xs">
            Pick the pickup window you want Opswarex to use by default for
            Easy-Ship orders on <strong>{account.account_name}</strong>. You can
            still override this per order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Slot start time</Label>
            <DateTimeInput
              value={form.slot_start_time}
              onChange={(e) => setForm({ ...form, slot_start_time: e.target.value })}
              data-testid="amazon-easyship-pref-start"
            />
          </div>
          <div>
            <Label className="text-xs">Slot end time</Label>
            <DateTimeInput
              value={form.slot_end_time}
              onChange={(e) => setForm({ ...form, slot_end_time: e.target.value })}
              data-testid="amazon-easyship-pref-end"
            />
          </div>
          <div>
            <Label className="text-xs">Label for this window (optional)</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. 10:00 AM – 2:00 PM"
              data-testid="amazon-easyship-pref-desc"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="amazon-easyship-pref-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save default
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
